import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Message sent! We\'ll get back to you within 24-48 hours.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Contact Us
          </h1>
          <p className="text-[#5A6B5C] text-lg mb-12">
            Have questions about our custom golf equipment? We're here to help.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Email</h3>
              <p className="text-[#5A6B5C] text-sm">nbond@tortlab.com</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Phone</h3>
              <p className="text-[#5A6B5C] text-sm">1-800-GOLF-FIT</p>
              <p className="text-[#5A6B5C] text-xs mt-1">Mon-Fri 9am-6pm EST</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Location</h3>
              <p className="text-[#5A6B5C] text-sm">Scottsdale, Arizona</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border border-[#E8E4DC]">
            <h2 className="text-2xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-[#334155] uppercase tracking-wider mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full h-12 px-4 bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg text-[#2C3E2D] focus:outline-none focus:border-[#4A6741]"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#334155] uppercase tracking-wider mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full h-12 px-4 bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg text-[#2C3E2D] focus:outline-none focus:border-[#4A6741]"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[#334155] uppercase tracking-wider mb-2">Subject</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full h-12 px-4 bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg text-[#2C3E2D] focus:outline-none focus:border-[#4A6741]"
                  placeholder="How can we help?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[#334155] uppercase tracking-wider mb-2">Message</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg text-[#2C3E2D] focus:outline-none focus:border-[#4A6741] resize-none"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>
              
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center space-x-2 h-14 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
